import { PlainDate, genPlainDateList } from "@sa-nu/shared-utils/date";

import type { AreasOnReservationFormQuery } from "../../../../../../__generated__/gql/graphql";
import type { CICOSelection } from "../../reservation-calendar";

/**
 * GraphQL クエリから取得したエリアデータの型
 */
type Area = AreasOnReservationFormQuery["areasForReservation"][number];
type Site = Area["sitesForReservation"][number];
type Accommodation = Site["accommodationsForReservation"][number];

/**
 * 空き状況を指定日付以前でフィルタリングする
 *
 * GraphQL から取得したエリアデータを、表示対象の日付範囲でフィルタリングします。
 * - lastNight より後の日付の空き状況は除外
 * - 空になったサイト・エリアも除外
 *
 * 戻り値は sites / accommodations プロパティに変換された形式
 */
export function filterAreasWithAvailability(
  areas: readonly Area[],
  lastNight: PlainDate,
): FilteredArea[] {
  return areas
    .map((area) => {
      const filteredSites = area.sitesForReservation
        .map((site) => {
          const filteredAccommodations = site.accommodationsForReservation.map(
            (accommodation) => {
              const filteredAvailabilities =
                accommodation.availabilities?.filter(
                  (ab) => PlainDate.compare(ab.date, lastNight) <= 0,
                );
              return {
                ...accommodation,
                availabilities: filteredAvailabilities,
              };
            },
          );
          return filteredAccommodations.length > 0
            ? {
                ...site,
                accommodations: filteredAccommodations,
              }
            : null;
        })
        .filter((site) => site !== null);
      return filteredSites.length > 0
        ? {
            ...area,
            sites: filteredSites,
          }
        : null;
    })
    .filter((area) => area !== null);
}

/**
 * フィルタリング後の型定義
 * GraphQL の Area/Site から ForReservation サフィックス付きプロパティを
 * sites/accommodations に変換した形式
 */
export type FilteredArea = Omit<Area, "sitesForReservation"> & {
  sites: FilteredSite[];
};

type FilteredSite = Omit<Site, "accommodationsForReservation"> & {
  accommodations: FilteredAccommodation[];
};

type FilteredAccommodation = Accommodation;

export type AccommodationCount = { available: number | null; total: number };

/**
 * エリアごとの部屋数を計算する
 * totalOnly が true の場合は available を null にする（CICO未選択時）
 */
export function getAccommodationCountMap(
  allAreas: FilteredArea[],
  displayAreas: FilteredArea[],
  totalOnly: boolean,
): Map<string, AccommodationCount> {
  const displayMap = totalOnly
    ? null
    : new Map(displayAreas.map((area) => [area.id, countAccommodations(area)]));
  return new Map(
    allAreas.map((area) => [
      area.id,
      {
        total: countAccommodations(area),
        available: displayMap ? (displayMap.get(area.id) ?? 0) : null,
      },
    ]),
  );
}

function countAccommodations(area: FilteredArea): number {
  return area.sites.reduce((sum, site) => sum + site.accommodations.length, 0);
}

/**
 * 選択/非選択エリア間のサジェスションディバイダーの表示状態を判定する
 *
 * 非選択エリアに部屋がある場合にディバイダーを表示する。
 * テキストの出し分け:
 * - totalOnly（エリアフィルタのみ）: 「希望のエリア外のお部屋」
 * - 条件あり＋選択エリアに空きあり: 「希望のエリア外にも条件の合うお部屋があります」
 * - 条件あり＋選択エリアに空きなし: 「希望のエリア外なら条件の合うお部屋があります」
 */
export type AreaSuggestionDivider =
  | { kind: "area-only" }
  | { kind: "with-conditions"; selectedAreaAllEmpty: boolean };

export function getAreaSuggestionDivider(params: {
  allAreas: readonly FilteredArea[];
  areaFilter: ReadonlySet<string>;
  accommodationCountMap: Map<string, AccommodationCount>;
  totalOnly: boolean;
}): AreaSuggestionDivider | null {
  const { allAreas, areaFilter, accommodationCountMap, totalOnly } = params;

  // エリアフィルタがない場合は表示しない
  if (areaFilter.size === 0) return null;

  // エリアフィルタがあってtotalOnlyの場合はarea-onlyを表示
  if (totalOnly) return { kind: "area-only" };

  // 非選択エリアが全て空なら提案するものがないので表示しない
  const unselectedAreaAllEmpty = allAreas
    .filter((a) => !areaFilter.has(a.slug))
    .every((a) => {
      const count = accommodationCountMap.get(a.id);
      const rooms = count?.available ?? 0;
      return rooms === 0;
    });
  if (unselectedAreaAllEmpty) return null;

  // 選択エリアが全て空きなしかどうか（誘導メッセージの出し分けに使う）
  const selectedAreaAllEmpty = allAreas
    .filter((a) => areaFilter.has(a.slug))
    .every((a) => (accommodationCountMap.get(a.id)?.available ?? 0) === 0);
  return { kind: "with-conditions", selectedAreaAllEmpty };
}

/**
 * CI/CO期間で部屋をフィルタリングする
 *
 * CI〜CO-1の全宿泊日で空きがある部屋のみ残す。
 * 空き部屋が0件のサイト・エリアは除外。
 *
 * alwaysVisibleAccommodationIds に含まれる部屋は
 * 空き状況に関わらず常に表示する（予約変更時の元の部屋や選択中の部屋の考慮）。
 */
export function filterAreasByCICO(
  areas: FilteredArea[],
  cico: CICOSelection,
  alwaysVisibleAccommodationIds: string[] = [],
): FilteredArea[] {
  const lastStayNight = cico.checkOut.subtract({ days: 1 });
  const stayNights = genPlainDateList(cico.checkIn, lastStayNight);

  return areas
    .map((area) => {
      const filteredSites = area.sites
        .map((site) => {
          const filteredAccommodations = site.accommodations.filter(
            (accommodation) => {
              if (!accommodation.availabilities) return false;

              // 常に表示する部屋はフィルタリングせず無条件で残す
              if (alwaysVisibleAccommodationIds.includes(accommodation.id)) {
                return true;
              }

              return stayNights.every((night) => {
                return accommodation.availabilities!.some(
                  (a) =>
                    PlainDate.compare(a.date, night) === 0 && a.isAvailable,
                );
              });
            },
          );
          return filteredAccommodations.length > 0
            ? { ...site, accommodations: filteredAccommodations }
            : null;
        })
        .filter((site) => site !== null);
      return filteredSites.length > 0
        ? { ...area, sites: filteredSites }
        : null;
    })
    .filter((area) => area !== null);
}
